import React, { useCallback, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import PageShell from '../components/site/PageShell';
import { images, dashboardLoginUrl } from '../config/esanzaContent';
import { apiPost } from '../lib/api';
import TurnstileWidget, { isTurnstileEnabled } from '../components/security/TurnstileWidget';

const PHONE_COUNTRIES = [
  ['RW', 'Rwanda', '+250'],
  ['KE', 'Kenya', '+254'],
  ['UG', 'Uganda', '+256'],
  ['TZ', 'Tanzania', '+255'],
  ['BI', 'Burundi', '+257'],
  ['CD', 'DR Congo', '+243'],
  ['GH', 'Ghana', '+233'],
  ['NG', 'Nigeria', '+234'],
  ['ZA', 'South Africa', '+27'],
  ['OT', 'Other', ''],
] as const;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;

const TryEsanzaPage: React.FC = () => {
  const [form, setForm] = useState({
    space_name: '',
    contact_name: '',
    contact_email: '',
    phone_dial_code: '+250',
    contact_phone_raw: '',
    country: 'Rwanda',
    country_code: 'RW',
    website: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const formStartedAt = useRef(Date.now());

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = form.contact_email.trim().toLowerCase();
    if (!EMAIL_RE.test(email)) {
      setError('Enter a valid email address.');
      return;
    }
    if (isTurnstileEnabled && !turnstileToken) {
      setError('Please complete the verification first.');
      return;
    }
    setIsSubmitting(true);
    setError('');
    try {
      const cleanPhone = form.contact_phone_raw.trim();
      await apiPost('/api/tenant-registration/request', {
        space_name: form.space_name,
        contact_name: form.contact_name,
        contact_email: email,
        contact_phone: `${form.phone_dial_code} ${cleanPhone}`.trim(),
        country: form.country,
        country_code: form.country_code,
        requested_modules: [],
        form_started_at: formStartedAt.current,
        turnstile_token: turnstileToken,
        website: form.website,
      });
      setIsSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Could not create your eSanza space request.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerify = useCallback((token: string) => {
    setTurnstileToken(token);
  }, []);

  const clearVerification = useCallback(() => {
    setTurnstileToken('');
  }, []);

  if (isSuccess) {
    return (
      <>
        <SEO
          title="Check your email | eSanza"
          description="Your eSanza workspace request was received. Check your email for the next step."
        />
        <PageShell>
          <section className="px-5 pb-20 pt-32 lg:px-8">
            <div className="mx-auto max-w-lg text-center">
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-lg border border-[#FFD700]/30 bg-white/5 p-8"
              >
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#FFD700] text-2xl font-black text-black">✓</div>
                <h1 className="mt-6 text-3xl font-black text-white">Check your email</h1>
                <p className="mt-3 text-white/70 leading-7">
                  We sent the next step to <strong className="text-white">{form.contact_email}</strong>. Use it to complete your organisation setup, then our team will review and activate the workspace.
                </p>
                <a
                  href={dashboardLoginUrl}
                  className="mt-8 inline-flex rounded-lg bg-[#FFD700] px-6 py-3 font-extrabold text-black transition-opacity hover:opacity-90"
                >
                  Login to existing workspace
                </a>
              </motion.div>
            </div>
          </section>
        </PageShell>
      </>
    );
  }

  return (
    <>
      <SEO
        title="Try eSanza | Start your workspace"
        description="Request an eSanza workspace for your organisation. Our team will email the next step."
        imagePath={images.traceability}
      />
      <PageShell>
        <section className="px-5 pb-20 pt-32 lg:px-8">
          <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
            <div>
              <p className="text-xs font-extrabold uppercase tracking-[0.24em] text-[#d4af37]">Try eSanza</p>
              <h1 className="mt-5 text-5xl font-black leading-[1] tracking-tight md:text-7xl">
                Start your workspace
              </h1>
              <p className="mt-6 max-w-xl text-lg leading-8 text-white/68">
                Fill in the basics. We will email the next step so you can configure your eSanza workspace and start managing your farming operations.
              </p>
              <div className="mt-8 rounded-lg border border-white/10 bg-white/[0.06] p-5">
                <p className="text-sm font-extrabold text-white">Already have a workspace?</p>
                <p className="mt-1 text-xs leading-5 text-white/60">Sign in with your email OTP.</p>
                <a
                  href={dashboardLoginUrl}
                  className="mt-3 inline-flex rounded-lg border border-[#FFD700]/40 px-4 py-2 text-sm font-extrabold text-[#FFD700] transition hover:bg-[#FFD700] hover:text-black"
                >
                  Login to workspace
                </a>
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.45, ease: 'easeOut' }}
              className="rounded-lg border border-white/10 bg-black/30 p-7 backdrop-blur md:p-8"
            >
              <form onSubmit={handleSubmit}>
                <input
                  tabIndex={-1}
                  autoComplete="off"
                  name="website"
                  value={form.website}
                  onChange={handleChange}
                  className="hidden"
                  aria-hidden="true"
                />
                <div className="rounded-lg border border-[#FFD700]/20 bg-[#FFD700]/10 p-4 mb-6">
                  <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-[#FFD700]">Create your space</p>
                  <p className="mt-2 text-sm leading-6 text-white/72">
                    Fill the basics. We email you the next step to complete your workspace configuration.
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="md:col-span-2">
                    <label className="text-xs font-extrabold uppercase tracking-[0.16em] text-white/60">
                      Organisation name
                    </label>
                    <input
                      required
                      name="space_name"
                      value={form.space_name}
                      onChange={handleChange}
                      className="mt-2 w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#FFD700]/40"
                      placeholder="e.g., Green Valley Cooperative"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-extrabold uppercase tracking-[0.16em] text-white/60">
                      Contact name
                    </label>
                    <input
                      required
                      name="contact_name"
                      value={form.contact_name}
                      onChange={handleChange}
                      className="mt-2 w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#FFD700]/40"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-extrabold uppercase tracking-[0.16em] text-white/60">
                      Email
                    </label>
                    <input
                      required
                      type="email"
                      name="contact_email"
                      value={form.contact_email}
                      onChange={handleChange}
                      className="mt-2 w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#FFD700]/40"
                      placeholder="you@organisation.com"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-extrabold uppercase tracking-[0.16em] text-white/60">
                      Phone
                    </label>
                    <input
                      required
                      type="tel"
                      name="contact_phone_raw"
                      value={form.contact_phone_raw}
                      onChange={handleChange}
                      className="mt-2 w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#FFD700]/40"
                      placeholder="790 311 401"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-extrabold uppercase tracking-[0.16em] text-white/60">
                      Country
                    </label>
                    <select
                      name="country_code"
                      value={form.country_code}
                      onChange={(e) => {
                        const selected = e.target.options[e.target.selectedIndex];
                        const dialCode = selected.getAttribute('data-dial') || '';
                        setForm((prev) => ({
                          ...prev,
                          country_code: e.target.value,
                          phone_dial_code: dialCode,
                          country: selected.getAttribute('data-country') || prev.country,
                        }));
                      }}
                      className="mt-2 w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#FFD700]/40"
                    >
                      {PHONE_COUNTRIES.map(([code, name, dial]) => (
                        <option key={code} value={code} data-country={name} data-dial={dial}>
                          {name}{dial ? ` (${dial})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mt-6 flex flex-col gap-4">
                  <TurnstileWidget action="tenant_registration" onVerify={handleVerify} onExpire={clearVerification} />
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="inline-flex w-full justify-center rounded-lg bg-[#FFD700] px-6 py-3 font-extrabold text-black transition-opacity hover:opacity-90 disabled:opacity-60"
                  >
                    {isSubmitting ? 'Submitting request...' : 'Create my eSanza space'}
                  </button>
                </div>
              </form>
              {error && <p className="mt-4 text-sm text-red-300">{error}</p>}
            </motion.div>
          </div>
        </section>
      </PageShell>
    </>
  );
};

export default TryEsanzaPage;
